import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, Phone, MapPin } from 'lucide-react';

const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-950" data-testid="privacy-policy-page">
      {/* Header */}
      <div className="bg-gray-900/50 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-b from-gray-900/50 to-transparent py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <Shield className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">Privacy Policy</h1>
              <p className="text-gray-400 text-sm mt-1">Last Updated: February 2025</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 lg:px-8 pb-20">
        <div className="prose prose-invert prose-emerald max-w-none">
          
          {/* Introduction */}
          <div className="bg-gray-900/30 rounded-2xl p-6 mb-8 border border-white/5">
            <p className="text-gray-300 leading-relaxed m-0">
              Gegadyne Energy Private Limited and its affiliates (hereinafter referred to as "Gegadyne Energy", "We", "Us" or "Our") 
              respect your privacy and appreciate your trust in us. This Privacy Policy explains how we collect, use, disclose, 
              and safeguard your information when you visit our website gegadyne.com or use our products and services.
            </p>
          </div>

          <p className="text-gray-400">
            This Privacy Policy is published in compliance with the Information Technology Act, 2000, the Information Technology 
            (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011, and the 
            Digital Personal Data Protection Act, 2023 of India.
          </p>

          <p className="text-gray-400">
            By accessing our website or using our services, you acknowledge that you have read, understood, and agree to be 
            bound by this Privacy Policy. If you do not agree with this Policy, please do not access or use our services.
          </p>

          {/* Table of Contents */}
          <div className="bg-gray-900/50 rounded-xl p-6 my-8 border border-white/10">
            <h3 className="text-white font-semibold mb-4 mt-0">This Policy covers:</h3>
            <ol className="text-gray-400 space-y-2 list-decimal pl-5 mb-0">
              <li><a href="#section-1" className="text-emerald-400 hover:text-emerald-300">Personal Data We Collect</a></li>
              <li><a href="#section-2" className="text-emerald-400 hover:text-emerald-300">Purpose and Legal Basis for Processing</a></li>
              <li><a href="#section-3" className="text-emerald-400 hover:text-emerald-300">How We Protect and Retain Your Data</a></li>
              <li><a href="#section-4" className="text-emerald-400 hover:text-emerald-300">Sharing, Transfer and Disclosure</a></li>
              <li><a href="#section-5" className="text-emerald-400 hover:text-emerald-300">Your Rights</a></li>
              <li><a href="#section-6" className="text-emerald-400 hover:text-emerald-300">Cookies and Similar Technologies</a></li>
              <li><a href="#section-7" className="text-emerald-400 hover:text-emerald-300">Data Storage Location</a></li>
              <li><a href="#section-8" className="text-emerald-400 hover:text-emerald-300">Updates to This Policy</a></li>
              <li><a href="#section-9" className="text-emerald-400 hover:text-emerald-300">Grievance Officer & Contact</a></li>
            </ol>
          </div>

          {/* Section 1 */}
          <section id="section-1" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-white mt-12 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-sm font-bold">1</span>
              Personal Data We Collect
            </h2>
            <p className="text-gray-400">
              Personal data refers to any information that relates to an identified or identifiable individual. We collect 
              personal data in accordance with the principles of lawfulness, fairness, transparency, purpose limitation, 
              data minimization, and accuracy as prescribed under Indian law.
            </p>
            
            <div className="overflow-x-auto my-6">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-800/50">
                    <th className="text-left text-white font-semibold p-4 border border-white/10">Category</th>
                    <th className="text-left text-white font-semibold p-4 border border-white/10">Data Collected</th>
                    <th className="text-left text-white font-semibold p-4 border border-white/10">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-gray-300 p-4 border border-white/10">Contact Information</td>
                    <td className="text-gray-400 p-4 border border-white/10">Name, email address, phone number, company name, designation, address</td>
                    <td className="text-gray-400 p-4 border border-white/10">Responding to enquiries, providing product information, customer support</td>
                  </tr>
                  <tr>
                    <td className="text-gray-300 p-4 border border-white/10">Website Usage Data</td>
                    <td className="text-gray-400 p-4 border border-white/10">IP address, browser type, operating system, pages visited, date/time of access</td>
                    <td className="text-gray-400 p-4 border border-white/10">Website analytics, improving user experience, security monitoring</td>
                  </tr>
                  <tr>
                    <td className="text-gray-300 p-4 border border-white/10">Career Applications</td>
                    <td className="text-gray-400 p-4 border border-white/10">Resume, educational qualifications, work experience, references</td>
                    <td className="text-gray-400 p-4 border border-white/10">Recruitment and employment purposes</td>
                  </tr>
                  <tr>
                    <td className="text-gray-300 p-4 border border-white/10">Business Information</td>
                    <td className="text-gray-400 p-4 border border-white/10">Company details, GST number, business requirements, procurement details</td>
                    <td className="text-gray-400 p-4 border border-white/10">B2B transactions, quotations, order processing</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 2 */}
          <section id="section-2" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-white mt-12 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-sm font-bold">2</span>
              Purpose and Legal Basis for Processing
            </h2>
            <p className="text-gray-400">We process your personal data for the following purposes:</p>
            <ul className="text-gray-400 space-y-2">
              <li>To respond to your enquiries and provide customer support</li>
              <li>To process and fulfil business orders and transactions</li>
              <li>To send product updates, newsletters, and marketing communications (with your consent)</li>
              <li>To improve our website, products, and services</li>
              <li>To comply with legal obligations under Indian law</li>
              <li>To protect our legitimate business interests and prevent fraud</li>
              <li>To process job applications and manage recruitment</li>
            </ul>
            <p className="text-gray-400">
              Our legal basis for processing your data includes: your consent, performance of a contract, 
              compliance with legal obligations, and our legitimate business interests.
            </p>
          </section>

          {/* Section 3 */}
          <section id="section-3" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-white mt-12 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-sm font-bold">3</span>
              How We Protect and Retain Your Data
            </h2>
            <p className="text-gray-400">
              Gegadyne Energy implements appropriate technical and organizational security measures to protect 
              your personal data against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="text-gray-400 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Regular security assessments and audits</li>
              <li>Employee training on data protection</li>
              <li>Physical security measures at our facilities</li>
            </ul>
            <p className="text-gray-400">
              We retain your personal data only for as long as necessary to fulfil the purposes for which it was collected, 
              or as required by applicable Indian laws. The retention period may vary depending on the nature of the data 
              and the purpose of processing.
            </p>
          </section>

          {/* Section 4 */}
          <section id="section-4" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-white mt-12 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-sm font-bold">4</span>
              Sharing, Transfer and Disclosure
            </h2>
            <p className="text-gray-400">
              Gegadyne Energy does not sell, rent, or trade your personal data. We may share your data in the following circumstances:
            </p>
            
            <h3 className="text-white font-semibold mt-6 mb-3">Sharing with Service Providers</h3>
            <p className="text-gray-400">
              We may share your data with trusted third-party service providers who assist us in operating our website, 
              conducting business, or servicing you, subject to appropriate data protection agreements.
            </p>

            <h3 className="text-white font-semibold mt-6 mb-3">Legal Requirements</h3>
            <p className="text-gray-400">
              We may disclose your personal data if required by law, court order, or governmental authority in India, 
              or when we believe disclosure is necessary to protect our rights, comply with a judicial proceeding, 
              or protect the safety of our users or the public.
            </p>

            <h3 className="text-white font-semibold mt-6 mb-3">Business Transfers</h3>
            <p className="text-gray-400">
              In the event of a merger, acquisition, or sale of assets, your personal data may be transferred as part 
              of the transaction, subject to continued protection under this Privacy Policy.
            </p>
          </section>

          {/* Section 5 */}
          <section id="section-5" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-white mt-12 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-sm font-bold">5</span>
              Your Rights
            </h2>
            <p className="text-gray-400">
              Under the Digital Personal Data Protection Act, 2023 and other applicable Indian laws, you have the following rights:
            </p>
            <ul className="text-gray-400 space-y-2">
              <li><strong className="text-white">Right to Access:</strong> You may request access to the personal data we hold about you.</li>
              <li><strong className="text-white">Right to Correction:</strong> You may request correction of inaccurate or incomplete personal data.</li>
              <li><strong className="text-white">Right to Erasure:</strong> You may request deletion of your personal data, subject to legal retention requirements.</li>
              <li><strong className="text-white">Right to Withdraw Consent:</strong> Where processing is based on consent, you may withdraw your consent at any time.</li>
              <li><strong className="text-white">Right to Grievance Redressal:</strong> You have the right to file a complaint with our Grievance Officer or the Data Protection Board of India.</li>
              <li><strong className="text-white">Right to Nominate:</strong> You may nominate another person to exercise your rights in case of death or incapacity.</li>
            </ul>
            <p className="text-gray-400">
              To exercise any of these rights, please contact our Grievance Officer using the details provided below.
            </p>
          </section>

          {/* Section 6 */}
          <section id="section-6" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-white mt-12 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-sm font-bold">6</span>
              Cookies and Similar Technologies
            </h2>
            <p className="text-gray-400">
              Our website uses cookies and similar technologies to enhance your browsing experience. Cookies are small 
              text files stored on your device that help us:
            </p>
            <ul className="text-gray-400 space-y-2">
              <li>Remember your preferences and settings</li>
              <li>Understand how you interact with our website</li>
              <li>Improve website performance and functionality</li>
              <li>Provide relevant content and analytics</li>
            </ul>
            <p className="text-gray-400">
              You can control cookie preferences through your browser settings. However, disabling certain cookies 
              may affect the functionality of our website.
            </p>
          </section>

          {/* Section 7 */}
          <section id="section-7" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-white mt-12 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-sm font-bold">7</span>
              Data Storage Location
            </h2>
            <p className="text-gray-400">
              Your personal data is primarily stored and processed within India, in compliance with Indian data 
              localization requirements. In limited circumstances where data may need to be transferred outside India, 
              we ensure that appropriate safeguards are in place as required under the Digital Personal Data Protection Act, 2023, 
              and that the receiving jurisdiction provides adequate data protection.
            </p>
          </section>

          {/* Section 8 */}
          <section id="section-8" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-white mt-12 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-sm font-bold">8</span>
              Updates to This Policy
            </h2>
            <p className="text-gray-400">
              Gegadyne Energy reserves the right to update or modify this Privacy Policy at any time. Any changes will be 
              posted on this page with an updated "Last Updated" date. We encourage you to review this Policy periodically 
              to stay informed about how we protect your information.
            </p>
            <p className="text-gray-400">
              For significant changes that materially affect your rights or how we process your data, we will provide 
              prominent notice on our website or through direct communication.
            </p>
          </section>

          {/* Section 9 - Contact */}
          <section id="section-9" className="scroll-mt-24">
            <h2 className="text-2xl font-bold text-white mt-12 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-sm font-bold">9</span>
              Grievance Officer & Contact
            </h2>
            <p className="text-gray-400">
              In accordance with the Information Technology Act, 2000 and the Digital Personal Data Protection Act, 2023, 
              we have appointed a Grievance Officer to address any concerns, complaints, or questions regarding 
              your personal data and this Privacy Policy.
            </p>
            
            <div className="bg-gray-900/50 rounded-xl p-6 my-6 border border-emerald-500/20">
              <h3 className="text-white font-semibold mb-4 mt-0">Grievance Officer</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-gray-300">
                  <Mail className="w-5 h-5 text-emerald-400" />
                  <span>privacy@gegadyne.com</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <Phone className="w-5 h-5 text-emerald-400" />
                  <span>+91 22 XXXX XXXX</span>
                </div>
                <div className="flex items-start gap-3 text-gray-300">
                  <MapPin className="w-5 h-5 text-emerald-400 mt-0.5" />
                  <span>
                    Gegadyne Energy Private Limited<br />
                    Mumbai, Maharashtra<br />
                    India
                  </span>
                </div>
              </div>
              <p className="text-gray-500 text-sm mt-4 mb-0">
                We will acknowledge your request within 48 hours and endeavour to resolve your grievance within 30 days.
              </p>
            </div>

            <p className="text-gray-400">
              If you are not satisfied with our response, you may escalate your complaint to the Data Protection Board of India 
              as established under the Digital Personal Data Protection Act, 2023.
            </p>
          </section>

          {/* Footer Note */}
          <div className="bg-gray-900/30 rounded-xl p-6 mt-12 border border-white/5">
            <p className="text-gray-500 text-sm m-0">
              This Privacy Policy is governed by and construed in accordance with the laws of India. 
              Any disputes arising out of or in connection with this Policy shall be subject to the exclusive 
              jurisdiction of the courts in Mumbai, Maharashtra, India.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
